// Shared types + tuning constants for the per-symbol bookStore (Phase 2 B3).
// Split out of bookStoreInternal.ts to keep every book module under 250 LOC.
// The constants are the EXACT values from the old singleton bookStore.ts —
// changing them changes the live order-book/depth behavior, so do not touch.

export type BookLevel = {
  price: number;
  qty: number;
};

/** A single point-in-time snapshot of aggregated book depth. */
export type BookSnapshot = {
  ts: number;
  /** Price bucket → total qty for bids. */
  bidBuckets: Map<number, number>;
  /** Price bucket → total qty for asks. */
  askBuckets: Map<number, number>;
};

/** How often to capture a snapshot (ms). (old bookStore.ts:18) */
export const SNAPSHOT_INTERVAL_MS = 5_000;
/** Maximum snapshots to keep (5s × 5000 ≈ 7 hours). (old bookStore.ts:20) */
export const MAX_SNAPSHOTS = 5_000;
/** Price bucket size as fraction of mid price. (old bookStore.ts:22) */
export const BUCKET_PCT = 0.003;

// TWAF grace period before clearing a vanished level's age. WS dropouts or
// single-message gaps shouldn't reset the clock — if the level comes back
// within 5s we keep its original firstSeenTs. (old bookStore.ts:62)
export const TWAF_VANISH_GRACE_MS = 5_000;

/** Per-symbol render data — the old singleton BookState minus its actions. */
export type SymbolBookState = {
  bids: BookLevel[];
  asks: BookLevel[];
  /** Monotonic counter — bumped on data change for selector stability. */
  version: number;
  /** rAF-coalesced counter for render consumers. */
  renderVersion: number;
  /** Rolling history of aggregated snapshots for Bookmap rendering. */
  history: BookSnapshot[];
  /** Version counter for history changes (avoids array reference checks). */
  historyVersion: number;
  /**
   * Per-side (price → firstSeenTs ms) map used by the TWAF (time-weighted
   * adaptive filter) in the S/R liquidity overlay. A level qualifies as a
   * "real" resting wall only after it's been seen for ≥60s; under that it's
   * rendered at reduced opacity so transient spoofing/flickers don't mislead.
   * Stamped on first appearance; cleared when a level is fully removed (qty=0)
   * for longer than the grace window.
   */
  bidAges: Map<number, number>;
  askAges: Map<number, number>;
};

/**
 * Per-symbol imperative scheduling state. Lives outside Zustand because it's
 * rAF/timeout bookkeeping, not render data — identical to how the old
 * singleton kept lastSnapshotTs / pendingRemoval / rAF ids at module scope.
 */
export type SymbolBookRuntime = {
  lastSnapshotTs: number;
  pendingBid: Map<number, number>;
  pendingAsk: Map<number, number>;
  renderFramePending: boolean;
  renderFrameId: number | null;
  renderTimeoutId: ReturnType<typeof setTimeout> | null;
};

/** Setter signature compatible with Zustand's set for the bookStore slice. */
export type BookStoreSet = (
  partial:
    | { bySymbol: Record<string, SymbolBookState> }
    | ((s: { bySymbol: Record<string, SymbolBookState> }) => {
        bySymbol: Record<string, SymbolBookState>;
      }),
) => void;

export type BookStoreGet = () => {
  bySymbol: Record<string, SymbolBookState>;
};

export function createEmptyBookState(): SymbolBookState {
  return {
    bids: [],
    asks: [],
    version: 0,
    renderVersion: 0,
    history: [],
    historyVersion: 0,
    bidAges: new Map(),
    askAges: new Map(),
  };
}
